import { useClient } from "@/composables/useClient";
import { deserializeCbiDto, serializeCbiDto, encodeRequestParam } from "../serialization/CbiDateCodec";
import type { AuthenticationApi } from "../../api/AuthenticationApi";
import type { AuthLoginRequest } from "../../generated/dto/AuthLoginRequest";
import type { AuthPasswordRequest } from "../../generated/dto/AuthPasswordRequest";
import type { AuthPermissionEscalationRequest } from "../../generated/dto/AuthPermissionEscalationRequest";
import type { AuthRefreshRequest } from "../../generated/dto/AuthRefreshRequest";
import type { AuthResetEmailRequest } from "../../generated/dto/AuthResetEmailRequest";
import type { AuthTokens } from "../../generated/dto/AuthTokens";
import type { JwtToken } from "../../generated/dto/JwtToken";
import type { ProtoLoginResult } from "../../generated/dto/ProtoLoginResult";

export class AuthenticationApiClient implements AuthenticationApi {
  constructor(private readonly client = useClient()) {}

  async authAsProtoPublic(): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/authentication/public/authasproto`);
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }

  async escalatePermissionsStaff(request: AuthPermissionEscalationRequest): Promise<JwtToken> {
    const { data } = await this.client.post<unknown>(`/authentication/staff/escalatepermissions`, serializeCbiDto(request, "generated.dto.AuthPermissionEscalationRequest"));
    return deserializeCbiDto<JwtToken>(data, "generated.dto.JwtToken");
  }

  async escalateSessionStaff(password: string): Promise<JwtToken> {
    const { data } = await this.client.post<unknown>(`/authentication/staff/escalatesession`, JSON.stringify(password));
    return deserializeCbiDto<JwtToken>(data, "generated.dto.JwtToken");
  }

  async escalateSessionMember(password: string): Promise<JwtToken> {
    const { data } = await this.client.post<unknown>(`/authentication/member/escalatesession`, JSON.stringify(password));
    return deserializeCbiDto<JwtToken>(data, "generated.dto.JwtToken");
  }

  async loginPublic(request: AuthLoginRequest): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/authentication/public/login`, serializeCbiDto(request, "generated.dto.AuthLoginRequest"));
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }

  async loginFromProtoMember(request: AuthLoginRequest): Promise<ProtoLoginResult> {
    const { data } = await this.client.post<unknown>(`/authentication/member/loginfromproto`, serializeCbiDto(request, "generated.dto.AuthLoginRequest"));
    return deserializeCbiDto<ProtoLoginResult>(data, "generated.dto.ProtoLoginResult");
  }

  async logoutPublic(request: AuthRefreshRequest): Promise<void> {
    await this.client.post(`/authentication/public/logout`, serializeCbiDto(request, "generated.dto.AuthRefreshRequest"));
  }

  async refreshPublic(request: AuthRefreshRequest): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/authentication/public/refresh`, serializeCbiDto(request, "generated.dto.AuthRefreshRequest"));
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }

  async sendPasswordResetPublic(request: AuthResetEmailRequest): Promise<boolean> {
    const { data } = await this.client.post<unknown>(`/authentication/public/sendpasswordreset`, serializeCbiDto(request, "generated.dto.AuthResetEmailRequest"));
    return data as boolean;
  }

  async updatePasswordStaff(request: AuthPasswordRequest): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/authentication/staff/updatepassword`, serializeCbiDto(request, "generated.dto.AuthPasswordRequest"));
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }

  async updatePasswordMember(request: AuthPasswordRequest): Promise<AuthTokens> {
    const { data } = await this.client.post<unknown>(`/authentication/member/updatepassword`, serializeCbiDto(request, "generated.dto.AuthPasswordRequest"));
    return deserializeCbiDto<AuthTokens>(data, "generated.dto.AuthTokens");
  }
}

export const useAuthenticationApi = (): AuthenticationApi => new AuthenticationApiClient();
